import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const CAROUSEL_GAP = 16;

function getSlidesToShow(width) {
  if (width < 576) return 1;
  if (width < 992) return 2;
  return 3;
}

function ServiceShowcaseCard({ service, variant = 'business' }) {
  return (
    <article className={`service-visual-card service-visual-card--${variant}`}>
      <Link to="/services" className="service-visual-card__link" aria-label={`Learn more about ${service.title}`}>
        <figure className="service-visual-card__media">
          <img src={service.image} alt={service.title} loading="lazy" />
          <div className="service-visual-card__overlay" aria-hidden="true"></div>

          <div className="service-visual-card__icon" aria-hidden="true">
            <ion-icon name={service.icon}></ion-icon>
          </div>

          <div className="service-visual-card__body">
            <h3 className="service-visual-card__title">{service.title}</h3>
            <p className="service-visual-card__desc">{service.description}</p>
            <span className="service-visual-card__cta">
              Explore service
              <ion-icon name="arrow-forward-outline" aria-hidden="true"></ion-icon>
            </span>
          </div>

          <div className="service-visual-card__arrow" aria-hidden="true">
            <ion-icon name="arrow-forward-outline"></ion-icon>
          </div>
        </figure>
      </Link>
    </article>
  );
}

function ServiceCarousel({ services, variant, title, badgeLabel, subtitle }) {
  const viewportRef = useRef(null);
  const touchStartX = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(() =>
    typeof window !== 'undefined' ? getSlidesToShow(window.innerWidth) : 3,
  );
  const [slideWidth, setSlideWidth] = useState(0);

  const gap = slidesToShow === 1 ? 0 : CAROUSEL_GAP;
  const maxIndex = Math.max(0, services.length - slidesToShow);
  const totalDots = maxIndex + 1;
  const showControls = services.length > slidesToShow;

  const measureViewport = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const viewportWidth = viewport.clientWidth;
    const nextSlideWidth = (viewportWidth - gap * (slidesToShow - 1)) / slidesToShow;
    setSlideWidth(nextSlideWidth > 0 ? nextSlideWidth : 0);
  }, [gap, slidesToShow]);

  useEffect(() => {
    const handleResize = () => {
      setSlidesToShow(getSlidesToShow(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    measureViewport();

    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measureViewport);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [measureViewport]);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null || !showControls) return;

    const diff = touchStartX.current - event.changedTouches[0].clientX;
    if (Math.abs(diff) > 48) {
      if (diff > 0) handleNext();
      else handlePrev();
    }

    touchStartX.current = null;
  };

  const offset = slideWidth > 0 ? currentIndex * (slideWidth + gap) : 0;

  return (
    <div className={`services-category services-category--${variant}`}>
      <div className="services-category-header">
        <div className="services-category-header__info">
          <span className={`services-category-badge ${variant === 'it' ? 'services-category-badge--it' : ''}`}>
            {badgeLabel}
          </span>
          <div className="services-category-header__text">
            <h3 className="services-category-title">{title}</h3>
            {subtitle && <p className="services-category-subtitle">{subtitle}</p>}
          </div>
        </div>

        {showControls && (
          <div className="services-carousel-toolbar">
            <span className="services-carousel-counter" aria-live="polite">
              {String(currentIndex + 1).padStart(2, '0')}
              <span className="services-carousel-counter__sep">/</span>
              {String(totalDots).padStart(2, '0')}
            </span>

            <div className="services-carousel-controls" aria-label={`${title} carousel controls`}>
              <button
                type="button"
                className="services-carousel-btn"
                onClick={handlePrev}
                aria-label={`Previous ${title.toLowerCase()}`}
              >
                <ion-icon name="chevron-back-outline"></ion-icon>
              </button>
              <button
                type="button"
                className="services-carousel-btn"
                onClick={handleNext}
                aria-label={`Next ${title.toLowerCase()}`}
              >
                <ion-icon name="chevron-forward-outline"></ion-icon>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="services-carousel">
        <div
          ref={viewportRef}
          className="services-carousel-viewport"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="services-carousel-track"
            style={{
              gap: `${gap}px`,
              transform: slideWidth > 0 ? `translateX(-${offset}px)` : undefined,
            }}
          >
            {services.map((service) => (
              <div
                key={service.title}
                className="services-carousel-slide"
                style={
                  slideWidth > 0
                    ? { flex: `0 0 ${slideWidth}px`, width: `${slideWidth}px`, minWidth: `${slideWidth}px` }
                    : undefined
                }
              >
                <ServiceShowcaseCard service={service} variant={variant} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {showControls && (
        <div className="services-carousel-dots" role="tablist" aria-label={`${title} slides`}>
          {Array.from({ length: totalDots }).map((_, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              className={`services-carousel-dot ${currentIndex === index ? 'active' : ''}`}
              aria-selected={currentIndex === index}
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ServiceCarousel;
